import React, { ReactNode } from "react";
import { cn } from "@/libs/utils";
import Container from "@/components/commons/Container";
import { ProductInTheBoxItem } from "@/components/tactsuit/constants/ProductInTheBox";
import Image from "next/image";

/*
 제품 패키지 구성 section
 */
export default function DisplayProductsInBoxSection({
  header,
  contents,
}: {
  header: ReactNode;
  contents: ReactNode;
}) {
  return (
    <section className="px-4 laptop:px-0">
      <Container
        className={
          "mx-auto max-w-screen-mdl rounded-2xl bg-coolgray-10 px-5 py-7 laptop:px-18 laptop:py-10"
        }
      >
        {header}
        {contents}
      </Container>
    </section>
  );
}

export function DisplayProductsInBox({
  className,
  products,
  title,
  /**
   * 글러브(DK3)처럼 아이템 적은 구성: 5열 좌측 그리드 대신 중앙정렬 3열 (Figma 27379:38994).
   * 모바일은 2+1 (Figma 27379:39204). ProductsInTheBox 모바일 패딩을 px-4(16px)로 맞춰
   * 가용폭 296px 에서 140px 카드 2개(140+16gap+140=296)가 한 줄에 들어간다. 패딩이 더 크면
   * (옛 p-10=40px → 가용 248px) 2개가 못 들어가 1열로 줄바꿈됐었음. gap 값은 양 레이아웃 모두 Figma 기준.
   */
  centered = false,
}: {
  className?: string;
  title?: string;
  products: ProductInTheBoxItem[];
  centered?: boolean;
}) {
  return (
    <section
      className={cn(
        "flex flex-col gap-2 text-center laptop:gap-5",
        !centered && "laptop:text-left",
      )}
    >
      {title && <h2 className="text-lg font-500">{title}</h2>}
      <div
        className={cn(
          centered
            ? // 카드 묶음(블록)은 중앙(grid justify-center), 블록 내부 카드는 좌측 정렬(셀 좌→우 채움).
              // auto-fit 으로 폭에 맞게 자동 wrap: 360px→2+1(Figma 27379:39204), 더 좁으면 1열(overflow 방지),
              // 데스크탑 3열(27379:38994). 2행 단일 카드는 묶음 좌측 칸에 붙음(줄마다 중앙되는 flex-wrap 과 다름).
              "grid grid-cols-[repeat(auto-fit,140px)] justify-center gap-x-4 gap-y-6 laptop:grid-cols-[repeat(auto-fit,192px)] laptop:gap-x-5 laptop:gap-y-5"
            : "grid grid-cols-2 gap-4 laptop:grid-cols-5 laptop:gap-2",
          className,
        )}
      >
        {products.map((p) => {
          return (
            <ProductInBoxCard key={p.title} product={p} centered={centered} />
          );
        })}
      </div>
    </section>
  );
}

export const ProductInBoxCard = ({
  product,
  centered = false,
}: {
  product: ProductInTheBoxItem;
  centered?: boolean;
}) => {
  const { title, src } = product;
  return (
    <div
      className={cn(
        "relative flex flex-col items-center gap-6",
        centered ? "w-[140px] laptop:w-[192px]" : "w-full",
      )}
    >
      <div className="relative flex aspect-square w-full max-w-[192px] items-center justify-center">
        <Image
          src={src}
          alt={"product image"}
          sizes="(max-width: 768px) 140px, 192px"
          className="object-cover"
          fill
        />
      </div>
      <p
        className={cn(
          "whitespace-pre-line text-center text-base",
          centered ? "w-full font-500 tracking-[0.1px]" : "w-32",
        )}
      >
        {title}
      </p>
    </div>
  );
};
